import React, { useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import './MonthCalendarView.css';

/**
 * MonthCalendarView - Reusable 2-month mini calendar component
 *
 * Features:
 * - Shows 2 months side-by-side
 * - Highlights blocked dates in red
 * - Highlights dates with bookings in yellow
 * - Click to select date range
 *
 * @param {Object} props
 * @param {DateTime} props.startMonth - Starting month to display (default: current month)
 * @param {Array<string>} props.blockedDates - Array of blocked date strings (YYYY-MM-DD format)
 * @param {Array<string>} props.bookingDates - Array of booking date strings (YYYY-MM-DD format)
 * @param {Object} props.selectedRange - { start: DateTime, end: DateTime }
 * @param {Function} props.onSelectRange - Callback when date range is selected
 * @param {boolean} props.rangeSelectionEnabled - Enable date range selection (default: true)
 */
const MonthCalendarView = ({
  startMonth = DateTime.now(),
  blockedDates = [],
  bookingDates = [],
  selectedRange = null,
  onSelectRange,
  rangeSelectionEnabled = true
}) => {
  const [rangeStart, setRangeStart] = useState(null);

  // Generate two consecutive months
  const months = useMemo(() => {
    const month1 = startMonth.startOf('month');
    const month2 = month1.plus({ months: 1 });
    return [month1, month2];
  }, [startMonth]);

  // Convert blocked dates to Map for color lookup
  // Supports both Array<string> (backward compatible) and Array<{date, color}>
  const blockedDatesMap = useMemo(() => {
    const map = new Map();
    blockedDates.forEach(item => {
      if (typeof item === 'string') {
        map.set(item, '#ef4444');  // Default red for backward compatibility
      } else {
        map.set(item.date, item.color);
      }
    });
    return map;
  }, [blockedDates]);

  const bookingDatesSet = useMemo(() => new Set(bookingDates), [bookingDates]);

  /**
   * Generate calendar grid for a month
   * Returns array of week arrays, each week has 7 day objects
   */
  const generateMonthGrid = (monthStart) => {
    const weeks = [];
    let currentDate = monthStart.startOf('week'); // Start on Monday

    // Generate 6 weeks to cover all possible month layouts
    for (let week = 0; week < 6; week++) {
      const weekDays = [];

      for (let day = 0; day < 7; day++) {
        const dateStr = currentDate.toISODate();
        const isCurrentMonth = currentDate.month === monthStart.month;
        const blockedColor = blockedDatesMap.get(dateStr);
        const isBlocked = !!blockedColor;
        const hasBooking = bookingDatesSet.has(dateStr);
        const isToday = currentDate.hasSame(DateTime.now(), 'day');
        const isWeekend = currentDate.weekday === 6 || currentDate.weekday === 7; // Saturday or Sunday

        // Check if date is in selected range
        const isInRange = selectedRange &&
          currentDate >= selectedRange.start &&
          currentDate <= selectedRange.end;

        const isRangeStart = selectedRange && currentDate.hasSame(selectedRange.start, 'day');
        const isRangeEnd = selectedRange && currentDate.hasSame(selectedRange.end, 'day');

        weekDays.push({
          date: currentDate,
          dateStr,
          dayNumber: currentDate.day,
          isCurrentMonth,
          isBlocked,
          blockedColor,
          hasBooking,
          isToday,
          isWeekend,
          isInRange,
          isRangeStart,
          isRangeEnd
        });

        currentDate = currentDate.plus({ days: 1 });
      }

      weeks.push(weekDays);
    }

    // Filter out weeks with zero current-month dates to reduce visual clutter
    return weeks.filter(week => week.some(day => day.isCurrentMonth));
  };

  /**
   * Handle date click for range selection
   */
  const handleDateClick = (dateObj) => {
    if (!rangeSelectionEnabled || !onSelectRange) return;

    // Prevent selection of ghosted dates (other-month dates)
    if (!dateObj.isCurrentMonth) return;

    if (!rangeStart) {
      // Start new range - immediately set as single-day range
      setRangeStart(dateObj.date);
      onSelectRange({ start: dateObj.date, end: dateObj.date });
    } else {
      // Complete range
      const start = rangeStart < dateObj.date ? rangeStart : dateObj.date;
      const end = rangeStart < dateObj.date ? dateObj.date : rangeStart;

      onSelectRange({ start, end });
      setRangeStart(null);
    }
  };

  /**
   * Handle double-click to clear selection
   */
  const handleDateDoubleClick = (dateObj) => {
    if (!rangeSelectionEnabled || !onSelectRange) return;

    // Prevent interaction with ghosted dates
    if (!dateObj.isCurrentMonth) return;

    // Clear the selection
    onSelectRange(null);
    setRangeStart(null);
  };

  /**
   * Get CSS classes for a day cell
   */
  const getDayClasses = (dayObj) => {
    const classes = ['calendar-day'];

    if (!dayObj.isCurrentMonth) classes.push('other-month');
    if (dayObj.isToday) classes.push('today');
    if (dayObj.isWeekend) classes.push('weekend');
    if (dayObj.isBlocked) classes.push('blocked');
    if (dayObj.hasBooking) classes.push('has-booking');
    if (dayObj.isInRange) classes.push('in-range');
    if (dayObj.isRangeStart) classes.push('range-start');
    if (dayObj.isRangeEnd) classes.push('range-end');
    if (rangeStart && dayObj.date.hasSame(rangeStart, 'day')) classes.push('range-start-temp');

    return classes.join(' ');
  };

  return (
    <div className="month-calendar-view">
      {months.map((month, monthIndex) => {
        const grid = generateMonthGrid(month);

        return (
          <div key={monthIndex} className="calendar-month">
            {/* Month Header */}
            <div className="calendar-month-header">
              <h3>{month.toFormat('MMMM yyyy')}</h3>
            </div>

            {/* Weekday Headers */}
            <div className="calendar-weekdays">
              {['M', 'T', 'W', 'Th', 'F', 'S', 'S'].map((day, index) => (
                <div
                  key={`${day}-${index}`}
                  className={`calendar-weekday ${(index === 5 || index === 6) ? 'weekend-header' : ''}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
              {grid.map((week, weekIndex) => (
                <div key={weekIndex} className="calendar-week">
                  {week.map((dayObj, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={getDayClasses(dayObj)}
                      onClick={() => handleDateClick(dayObj)}
                      onDoubleClick={() => handleDateDoubleClick(dayObj)}
                      style={dayObj.isBlocked ? {
                        border: `2px solid ${dayObj.blockedColor}`,
                        backgroundColor: `${dayObj.blockedColor}20`,
                        boxShadow: `0 0 0 1px ${dayObj.blockedColor}40`
                      } : {}}
                      title={
                        dayObj.isBlocked ? 'Blocked date' :
                        dayObj.hasBooking ? 'Has bookings' :
                        ''
                      }
                    >
                      <span className="day-number">{dayObj.dayNumber}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MonthCalendarView;
